import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "./api";

const AuthContext = createContext(null);

/**
 * Auth + welcome-modal state. The welcomeUser is set right after a successful
 * login/signup/guest and cleared either by the user dismissing the modal or by
 * the auto-close timer in WelcomeModal.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcomeUser, setWelcomeUser] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ba_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const res = await apiClient.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      localStorage.removeItem("ba_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem("ba_token", res.data.token);
    setUser(res.data.user);
    setWelcomeUser(res.data.user);
    return res.data.user;
  };

  const register = async (email, username, password) => {
    const res = await apiClient.post("/auth/register", { email, username, password });
    localStorage.setItem("ba_token", res.data.token);
    setUser(res.data.user);
    setWelcomeUser(res.data.user);
    return res.data.user;
  };

  const playAsGuest = async (apiResult) => {
    // Caller has already POSTed /auth/guest and stored the token.
    const fresh = await refresh();
    setWelcomeUser(fresh || apiResult?.user || null);
  };

  const loginWithGoogle = async (code, state, redirectUri) => {
    // Exchange Google OAuth code for JWT token
    const res = await apiClient.post("/auth/google/exchange", {
      code,
      state,
      redirect_uri: redirectUri,
    });
    localStorage.setItem("ba_token", res.data.token);
    setUser(res.data.user);
    setWelcomeUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("ba_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, welcomeUser, setWelcomeUser, playAsGuest, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
